/**
 * ItemMetadataModal — view and edit an item's metadata (DE-247).
 *
 * Until now the library's metadata could be neither SEEN nor EDITED in the
 * Portal. It had drifted from the repo — the sidecars in S3 were the June
 * originals with no German `i18n` at all — and nothing surfaced that, so the
 * first real content deploy staged a job that would have wiped every German
 * testimonial title.
 *
 * Two things this panel is deliberate about:
 *
 *  1. It shows what the DEVICE will read, not a prettified summary. The JSON
 *     preview is the actual `<stem>.json` shape (`ContentMeta.qml` reads exactly
 *     these keys), because the whole class of bug here is the Portal showing
 *     something other than what ships.
 *
 *  2. Saving rewrites the file as well as the columns. The backend does that in
 *     one transaction; this component must never offer a "just update the
 *     display" path, or it recreates the divergence it exists to cure.
 */
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEditItemMetadata, useMoveItem, useRetireAsset, useUploadPresign, type ContentItem,
} from '../api/hooks';
import { putToS3, pollUpload } from '../components/s3upload';

/** Languages the device UI can be set to. `zxx` is not a UI language — it is an
 *  answer about the CONTENT, and a real one. */
const UI_LANGS = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const SPOKEN_OPTIONS = [
  { code: '', label: 'Unknown — never filtered' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'zxx', label: 'No speech (music / wordless) — never filtered' },
];

export function ItemMetadataModal(
  { item, onClose }: { item: ContentItem; onClose: () => void },
) {
  const edit = useEditItemMetadata();
  const presign = useUploadPresign();
  const move = useMoveItem();
  const qc = useQueryClient();
  const [subFile, setSubFile] = useState<File | null>(null);
  const [subLang, setSubLang] = useState('de');
  const [subBusy, setSubBusy] = useState<string | null>(null);
  const retire = useRetireAsset();

  /**
   * Remove one subtitle track.
   *
   * Retiring is REFUSED while a profile still references the file, and that
   * refusal is surfaced verbatim rather than swallowed — it is not a bug to work
   * around. buildJobDocument filters `deleted_at IS NULL`, so retiring a
   * referenced asset would silently DROP the file from that profile's manifest,
   * and a device deploying it would lose the track with nothing to explain why.
   * The way to take content off a device is to deploy a profile without it; the
   * way to take it out of the library is to retire it once no profile needs it.
   */
  const removeTrack = async (assetId: string, lang: string) => {
    setErr(null);
    if (!window.confirm(
      `Remove the ${lang.toUpperCase()} subtitle from ${item.stem}?\n\n`
      + 'It stops being offered for new profiles. Devices that already have it keep '
      + 'it until a profile without it is deployed.')) return;
    // Two steps when a LIVE profile references it: the server's 409 NAMES the
    // profiles and states the consequence, so it is shown verbatim before the
    // operator decides. Retried only on a 409, and only when force was not
    // already set, so an unrelated failure can never be escalated.
    const attempt = async (force: boolean): Promise<void> => {
      setSubBusy(`Removing ${lang.toUpperCase()}…`);
      try {
        await retire.mutateAsync({ id: assetId, force });
        await qc.invalidateQueries({ queryKey: ['content', 'items'] });
        setSubBusy(null);
      } catch (e: any) {
        setSubBusy(null);
        if (!force && e?.status === 409) {
          if (window.confirm(`${e.message}\n\nRemove the ${lang.toUpperCase()} subtitle anyway?`))
            return attempt(true);
          return;
        }
        setErr(e?.message ?? 'could not remove the track');
      }
    };
    await attempt(false);
  };

  /**
   * Attach a subtitle to THIS item.
   *
   * A subtitle is a property of a video, not an asset in its own right — the
   * standalone upload path existed only because the UI modelled it as one, and
   * it forced the operator to name the item, pick it from a list, and have their
   * filename silently discarded.
   *
   * Two mechanics worth knowing:
   *  • the stored filename is DERIVED server-side from {itemId, language}, so a
   *    mis-named file cannot silently become its own stray item;
   *  • every upload lands in `storage` regardless, so once the Lambda has
   *    registered it we Move it into this item's collection. Without that step
   *    the track would sit in staging and never reach a device.
   */
  const attachSubtitle = async () => {
    if (!subFile) return;
    setErr(null);
    try {
      setSubBusy('Uploading…');
      const { uploadUrl, uploadId } = await presign.mutateAsync({
        filename: subFile.name, format: 'sidecar',
        itemId: item.itemId, language: subLang,
      });
      await putToS3(uploadUrl, subFile, () => {});
      setSubBusy('Registering…');
      const st = await pollUpload(uploadId);
      if (st?.status === 'failed') throw new Error(st.error ?? 'the processor rejected the file');
      if (item.collection !== 'storage') {
        setSubBusy('Filing…');
        await move.mutateAsync({ itemId: `storage/${item.stem}`, collection: item.collection });
      }
      await qc.invalidateQueries({ queryKey: ['content', 'items'] });
      setSubFile(null);
      setSubBusy(null);
    } catch (e: any) {
      setSubBusy(null);
      setErr(e?.message ?? 'could not attach the subtitle');
    }
  };

  const [title, setTitle] = useState(item.title ?? '');
  const [description, setDescription] = useState(item.description ?? '');
  const [language, setLanguage] = useState(item.language ?? '');
  const [i18n, setI18n] = useState<Record<string, { title?: string; description?: string }>>(
    () => JSON.parse(JSON.stringify(item.titleI18n ?? {})),
  );
  const [err, setErr] = useState<string | null>(null);

  const setLoc = (lang: string, field: 'title' | 'description', v: string) =>
    setI18n((prev) => {
      const entry: { title?: string; description?: string } = { ...(prev[lang] ?? {}) };
      if (v.trim() === '') delete entry[field];
      else entry[field] = v;
      const next = { ...prev };
      // Drop the language entirely once it has no overrides left, so the written
      // document never carries an empty `{"de":{}}` that reads as "translated".
      if (Object.keys(entry).length === 0) delete next[lang];
      else next[lang] = entry;
      return next;
    });

  // Exactly what will be written, rendered live. Keys are omitted when empty,
  // matching the backend — so the operator sees the real document, including the
  // absence of a field, rather than a form's idea of it.
  const preview = useMemo(() => {
    const doc: Record<string, unknown> = {};
    if (title.trim()) doc.title = title.trim();
    if (description.trim()) doc.description = description.trim();
    if (language.trim()) doc.language = language.trim();
    if (Object.keys(i18n).length) doc.i18n = i18n;
    return JSON.stringify(doc, null, 2);
  }, [title, description, language, i18n]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await edit.mutateAsync({
        itemId: item.itemId,
        title: title.trim() || null,
        description: description.trim() || null,
        language: language.trim() || null,
        i18n: Object.keys(i18n).length ? i18n : null,
      });
      onClose();
    } catch (e2: any) {
      setErr(e2?.message ?? 'save failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal">
      <form onSubmit={save} className="card w-full max-w-3xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h2 className="text-lg font-semibold">{item.stem}</h2>
          <p className="text-xs text-muted">
            {item.collection} · {item.fileCount} file{item.fileCount === 1 ? '' : 's'}
            {item.subtitleLangs?.length
              ? ` · subtitles: ${item.subtitleLangs.map((l) => l.toUpperCase()).join(', ')}`
              : ' · no subtitles'}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <p className="text-[11px] uppercase tracking-wide text-muted">Default (English)</p>
            <label className="block text-sm">
              Title
              <input className="input mt-1 w-full" value={title}
                     onChange={(e) => setTitle(e.target.value)} />
            </label>
            <label className="block text-sm">
              Description
              <textarea className="input mt-1 w-full" rows={3} value={description}
                        onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label className="block text-sm">
              Spoken language
              <select className="input mt-1 w-full" value={language}
                      onChange={(e) => setLanguage(e.target.value)}>
                {SPOKEN_OPTIONS.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
            </label>
            {/* The filter rule is not obvious and getting it wrong hides real
                content, so state it where the choice is made. */}
            <p className="text-[11px] text-muted">
              Used by the device's wrong-language filter: a clip is hidden only when its spoken
              language is known, differs from the UI language, and no subtitle exists in that
              language. Unknown and “no speech” are never hidden.
            </p>
          </div>

          <div className="space-y-3">
            {UI_LANGS.filter((l) => l.code !== 'en').map((l) => (
              <div key={l.code} className="space-y-3">
                <p className="text-[11px] uppercase tracking-wide text-muted">
                  {l.label} override
                </p>
                <label className="block text-sm">
                  Title
                  <input className="input mt-1 w-full" value={i18n[l.code]?.title ?? ''}
                         onChange={(e) => setLoc(l.code, 'title', e.target.value)} />
                </label>
                <label className="block text-sm">
                  Description
                  <textarea className="input mt-1 w-full" rows={3}
                            value={i18n[l.code]?.description ?? ''}
                            onChange={(e) => setLoc(l.code, 'description', e.target.value)} />
                </label>
                <p className="text-[11px] text-muted">
                  Leave blank to fall back to the default — the device falls back per FIELD, so a
                  translated title with no translated description is a valid, supported state.
                </p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-muted mb-1">
            What the device will read — <code>{item.stem}.json</code>
          </p>
          <pre className="text-[11px] bg-[var(--surface-2)] rounded p-2 overflow-x-auto">
            {preview === '{}' ? '{}   (no metadata — the grid falls back to the filename)' : preview}
          </pre>
        </div>

        <div className="border-t border-[var(--border)] pt-3">
          <p className="text-[11px] uppercase tracking-wide text-muted mb-1">Subtitles</p>
          {item.subtitleTracks?.length ? (
            <div className="flex flex-wrap gap-1 mb-2">
              {item.subtitleTracks.map((t) => (
                <span key={t.assetId}
                      className="pill-accent inline-flex items-center gap-1"
                      title={t.relpath}>
                  CC {t.language.toUpperCase()}
                  <button
                    type="button"
                    aria-label={`Remove the ${t.language.toUpperCase()} subtitle`}
                    title={`Remove the ${t.language.toUpperCase()} subtitle`}
                    disabled={!!subBusy}
                    onClick={() => void removeTrack(t.assetId, t.language)}
                    className="ml-0.5 leading-none opacity-60 hover:opacity-100 disabled:opacity-30"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted mb-2">No subtitle tracks yet.</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input type="file" accept=".vtt" className="text-xs"
                   onChange={(e) => setSubFile(e.target.files?.[0] ?? null)} />
            <select className="input text-xs" value={subLang}
                    onChange={(e) => setSubLang(e.target.value)}>
              {UI_LANGS.map((l) => (
                <option key={l.code} value={l.code}>{l.label} ({l.code})</option>
              ))}
            </select>
            <button type="button" className="btn-ghost text-xs"
                    disabled={!subFile || !!subBusy}
                    onClick={() => void attachSubtitle()}>
              {subBusy ?? 'Attach subtitle'}
            </button>
          </div>
          <p className="text-[11px] text-muted mt-1">
            Stored as <code>{item.stem}.{subLang}.vtt</code> — the name is derived from this item
            and language, so the file you pick can be called anything.
            {item.subtitleLangs?.includes(subLang) && ' Replaces the existing track.'}
          </p>
        </div>

        {err && <p className="text-xs text-[var(--danger)]">{err}</p>}

        <div className="flex justify-between items-center gap-2">
          <p className="text-[11px] text-muted">
            Saving rewrites the sidecar file too, so devices receive exactly this.
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" disabled={edit.isPending}>
              {edit.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
